export const ROLE_CODES = {
  SYSTEM_SUPER_ADMIN: 'system_super_admin',
  SYSTEM_ADMIN: 'system_admin',
  SYSTEM_OPERATOR: 'system_operator',
  BRAND_MANAGER: 'brand_manager',
  BRAND_OPERATOR: 'brand_operator',
  AGENCY_MANAGER: 'agency_manager',
  AGENCY_OPERATOR: 'agency_operator',
}

const LEGACY_ROLE_ALIASES = {
  fly_super_admin_test: ROLE_CODES.SYSTEM_SUPER_ADMIN,
  brand_manager_test: ROLE_CODES.BRAND_MANAGER,
  brand_operator_test: ROLE_CODES.BRAND_OPERATOR,
  agency_manager_test: ROLE_CODES.AGENCY_MANAGER,
  agency_operator_test: ROLE_CODES.AGENCY_OPERATOR,
}

export const ROLE_LABELS = {
  [ROLE_CODES.SYSTEM_SUPER_ADMIN]: 'Super Admin',
  [ROLE_CODES.SYSTEM_ADMIN]: 'Admin',
  [ROLE_CODES.SYSTEM_OPERATOR]: 'System Operator',
  [ROLE_CODES.BRAND_MANAGER]: 'Brand Manager',
  [ROLE_CODES.BRAND_OPERATOR]: 'Brand Operator',
  [ROLE_CODES.AGENCY_MANAGER]: 'Agency Manager',
  [ROLE_CODES.AGENCY_OPERATOR]: 'Agency Operator',
}

const ROLE_RANK = {
  [ROLE_CODES.SYSTEM_SUPER_ADMIN]: 100,
  [ROLE_CODES.SYSTEM_ADMIN]: 80,
  [ROLE_CODES.SYSTEM_OPERATOR]: 55,
  [ROLE_CODES.BRAND_MANAGER]: 50,
  [ROLE_CODES.AGENCY_MANAGER]: 40,
  [ROLE_CODES.BRAND_OPERATOR]: 30,
  [ROLE_CODES.AGENCY_OPERATOR]: 20,
}

const BRAND_MANAGER_ASSIGNABLE = new Set([ROLE_CODES.BRAND_OPERATOR, ROLE_CODES.AGENCY_MANAGER, ROLE_CODES.AGENCY_OPERATOR])

export const normalizeRoleCode = code => LEGACY_ROLE_ALIASES[code] || code

export const decodeJwtPayload = token => {
  if (!token || typeof token !== 'string') return null
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    return JSON.parse(window.atob(padded))
  } catch (error) {
    return null
  }
}

export const buildAccessProfile = (token, memberships = []) => {
  const user = decodeJwtPayload(token) || {}
  const permissions = new Set()
  const roleCodes = new Set()
  const orgTypes = new Set()
  const policies = []

  memberships.forEach(membership => {
    if (membership?.orgType) orgTypes.add(membership.orgType)
    if (membership?.role?.code) roleCodes.add(normalizeRoleCode(membership.role.code))
    ;(membership?.role?.permissions || []).forEach(permission => permissions.add(permission))
    if (membership?.role?.policy) policies.push(membership.role.policy)
    if (membership?.policy) policies.push(membership.policy)
  })

  const accountType = user.accountType
  const scope = user.scope || []
  const isSuperAdmin = permissions.has('*') || roleCodes.has(ROLE_CODES.SYSTEM_SUPER_ADMIN) || user.sub === 'platform-admin' || scope.includes('platform:admin')
  const isSystemAdmin = !isSuperAdmin && roleCodes.has(ROLE_CODES.SYSTEM_ADMIN)
  const isSystemOperator = roleCodes.has(ROLE_CODES.SYSTEM_OPERATOR) || accountType === 'fly_operator'
  const isFlyAdmin = isSuperAdmin || isSystemAdmin
  const isBrandUser = accountType === 'brand_user' || orgTypes.has('brand')
  const isAgencyUser = accountType === 'agency_user' || orgTypes.has('agency')
  const isOperator = isSystemOperator || roleCodes.has(ROLE_CODES.BRAND_OPERATOR) || roleCodes.has(ROLE_CODES.AGENCY_OPERATOR)
  const isBrandManager = roleCodes.has(ROLE_CODES.BRAND_MANAGER) || (isBrandUser && permissions.has('brand:update:own'))
  const isAgencyManager = roleCodes.has(ROLE_CODES.AGENCY_MANAGER) || (isAgencyUser && permissions.has('agency:update:own'))

  return {
    user,
    memberships,
    permissions,
    roleCodes,
    orgTypes,
    policies,
    accountType,
    isSuperAdmin,
    isSystemAdmin,
    isSystemOperator,
    isFlyAdmin,
    isBrandUser,
    isAgencyUser,
    isOperator,
    isBrandManager,
    isAgencyManager,
  }
}

export const hasPermission = (profile, permission) => {
  if (!profile) return false
  if (profile.permissions?.has('*') || profile.permissions?.has(permission)) return true
  const grantedParts = permission.split(':')
  return Array.from(profile.permissions || []).some(granted => {
    if (granted.endsWith('*')) return permission.startsWith(granted.slice(0, -1))
    const parts = granted.split(':')
    return parts.length === grantedParts.length && parts[0] === grantedParts[0] && parts[1] === grantedParts[1] && parts[2] === 'all'
  })
}

export const canReadSystemMetrics = profile => Boolean(
  profile &&
  hasPermission(profile, 'system:metrics:read') &&
  (profile.isSuperAdmin || profile.isSystemAdmin)
)

export const canAccessNavKey = (profile, key) => {
  if (!profile) return false
  if (key === 'dashboard' || key === 'dashboard.default') return true

  // Game versions/files are managed inside each Campaign workspace, not as global modules.
  if (['game-versions', 'assets'].includes(key)) return false

  if (key === 'activity-logs') return hasPermission(profile, 'log:read:all')
  if (key === 'system-metrics') return canReadSystemMetrics(profile)

  if (profile.isSuperAdmin) return true
  if (profile.isSystemAdmin) return ['users', 'brands', 'agencies', 'roles', 'memberships', 'campaigns', 'collaborators', 'realtime', 'reports'].includes(key)

  const commonCampaignOps = ['dashboard', 'campaigns', 'collaborators', 'realtime', 'reports']
  if (commonCampaignOps.includes(key)) return true

  if (key === 'brands') return profile.isBrandManager || hasPermission(profile, 'brand:update:own_limited') || hasPermission(profile, 'brand:read:own')
  if (key === 'agencies') return hasPermission(profile, 'agency:read:own') || hasPermission(profile, 'agency:update:own') || profile.isBrandManager || hasPermission(profile, 'campaign-team:assign_agency') || hasPermission(profile, 'campaign-team:read')

  // Global setup must not be visible to Brand/Agency/Operator users.
  if (['users', 'roles', 'memberships'].includes(key)) return false

  return false
}

export const getNavTitle = (profile, nav) => {
  if (!profile || profile.isSuperAdmin || profile.isSystemAdmin) return nav.title
  if (profile.isSystemAdmin && nav.key === 'fly-admin') return 'sidenav.flyAdminSetup'
  if (nav.key === 'fly-admin') return 'sidenav.accountWorkspace'
  if (nav.key === 'brands') return 'sidenav.myBrand'
  if (nav.key === 'agencies') return profile.isAgencyUser ? 'sidenav.myAgency' : 'sidenav.agencyDirectory'
  if (nav.key === 'campaign-ops') return profile.isAgencyUser ? 'sidenav.assignedCampaignOps' : 'sidenav.brandCampaignOps'
  return nav.title
}

export const filterNavigationByAccess = (navItems, profile) => {
  const decorate = nav => ({ ...nav, title: getNavTitle(profile, nav) })
  return navItems
    .map(nav => {
      const children = nav.submenu?.length ? filterNavigationByAccess(nav.submenu, profile) : []
      if (nav.isGroupTitle) return children.length ? decorate({ ...nav, submenu: children }) : null
      return canAccessNavKey(profile, nav.key) ? decorate({ ...nav, submenu: children }) : null
    })
    .filter(Boolean)
}

export const canCreateResource = (profile, resource) => {
  if (!profile) return false
  if (profile.isSuperAdmin) return true
  if (resource === 'users') return hasPermission(profile, 'user:create')
  if (resource === 'roles') return hasPermission(profile, 'role:create')
  if (resource === 'memberships') return hasPermission(profile, 'role:assign')
  if (resource === 'brands') return hasPermission(profile, 'brand:create')
  if (resource === 'agencies') return hasPermission(profile, 'agency:create')
  if (resource === 'collaborators') return profile.isAgencyManager || hasPermission(profile, 'campaign-team:assign_agency') || hasPermission(profile, 'campaign-team:assign_brand_member')
  if (resource === 'campaigns') return hasPermission(profile, 'campaign:create:all') || hasPermission(profile, 'campaign:create:own_brand')
  if (resource === 'game-versions') return hasPermission(profile, 'game-version:create:own_brand') || hasPermission(profile, 'game-version:create:assigned')
  if (resource === 'assets') return hasPermission(profile, 'asset:create:own_brand') || hasPermission(profile, 'asset:create:assigned')
  return false
}

export const canUpdateResource = (profile, resource) => {
  if (!profile) return false
  if (profile.isSuperAdmin) return true
  if (resource === 'users') return hasPermission(profile, 'user:update')
  if (resource === 'roles') return hasPermission(profile, 'role:update')
  if (resource === 'memberships') return hasPermission(profile, 'role:assign')
  if (resource === 'brands') return hasPermission(profile, 'brand:update') || hasPermission(profile, 'brand:update:own') || hasPermission(profile, 'brand:update:own_limited')
  if (resource === 'agencies') return hasPermission(profile, 'agency:update') || hasPermission(profile, 'agency:update:own')
  if (resource === 'collaborators') return hasPermission(profile, 'campaign-team:update_role') || hasPermission(profile, 'campaign-team:assign_agency') || hasPermission(profile, 'campaign-team:assign_brand_member')
  if (resource === 'campaigns') return hasPermission(profile, 'campaign:update:all') || hasPermission(profile, 'campaign:update:own_brand') || hasPermission(profile, 'campaign:update:assigned_limited')
  if (resource === 'game-versions') return hasPermission(profile, 'game-version:update:own_brand') || hasPermission(profile, 'game-version:update:assigned')
  if (resource === 'assets') return hasPermission(profile, 'asset:update:own_brand') || hasPermission(profile, 'asset:update:assigned')
  return false
}

export const canMutateResource = (profile, resource) => canCreateResource(profile, resource) || canUpdateResource(profile, resource)

export const canDeleteResource = (profile, resource) => {
  if (!profile) return false
  if (profile.isSuperAdmin) return true
  if (profile.isSystemAdmin) return false
  if (resource === 'collaborators') return hasPermission(profile, 'campaign-team:unassign_agency')
  if (resource === 'campaigns') return hasPermission(profile, 'campaign:delete:all') || hasPermission(profile, 'campaign:delete:own_brand')
  if (resource === 'game-versions') return hasPermission(profile, 'game-version:delete:own_brand')
  if (resource === 'assets') return hasPermission(profile, 'asset:delete:own_brand')
  return false
}

export const canResetUserPassword = profile => Boolean(profile?.isSuperAdmin || profile?.isSystemAdmin)

export const canManageAgencyMembers = profile => Boolean(
  profile?.isSuperAdmin ||
  profile?.isSystemAdmin ||
  hasPermission(profile, 'role:assign') ||
  hasPermission(profile, 'agency:update:own')
)

export const canPreviewEnvironmentVersion = (profile, environment = 'develop') => {
  if (!profile) return false
  if (profile.isSuperAdmin || profile.isSystemAdmin) return true
  if (environment === 'develop') return canUpdateResource(profile, 'game-versions') || hasPermission(profile, 'game-version:preview:assigned')
  if (environment === 'staging') return profile.isBrandManager || profile.isAgencyManager || hasPermission(profile, 'game-version:read:assigned')
  return profile.isBrandManager || hasPermission(profile, 'game-version:read:own_brand')
}

export const canSubmitVersionReview = profile => Boolean(
  profile?.isSuperAdmin ||
  profile?.isSystemAdmin ||
  profile?.isSystemOperator ||
  profile?.isBrandManager ||
  profile?.roleCodes?.has(ROLE_CODES.BRAND_OPERATOR) ||
  profile?.isAgencyManager ||
  hasPermission(profile, 'game-version:submit-review:own_brand')
)

export const canApproveVersionReview = profile => Boolean(profile?.isSuperAdmin || profile?.isSystemAdmin || profile?.isBrandManager)

export const canPromoteVersionToProduction = profile => Boolean(profile?.isSuperAdmin || profile?.isSystemAdmin || profile?.isBrandManager)

export const canPublishVersions = profile => Boolean(profile?.isSuperAdmin || profile?.isSystemAdmin || profile?.isBrandManager)

export const canEditCampaign = profile => Boolean(
  profile?.isSuperAdmin ||
  profile?.isSystemAdmin ||
  profile?.isBrandManager ||
  profile?.isAgencyManager ||
  profile?.isSystemOperator ||
  hasPermission(profile, 'campaign:update:assigned_limited') ||
  hasPermission(profile, 'campaign:update:own_brand') ||
  hasPermission(profile, 'campaign:update:all')
)

export const getPrimaryRoleCode = profile => {
  if (!profile) return null
  return Array.from(profile.roleCodes || []).sort((a, b) => (ROLE_RANK[b] || 0) - (ROLE_RANK[a] || 0))[0] || null
}

export const canSeeRole = (profile, role) => {
  if (!profile || !role) return false
  const code = normalizeRoleCode(role.code)
  if (profile.isSuperAdmin) return true
  if (code === ROLE_CODES.SYSTEM_SUPER_ADMIN || role.permissions?.includes('*')) return false
  if (profile.isSystemAdmin) return (ROLE_RANK[code] || 0) <= ROLE_RANK[ROLE_CODES.SYSTEM_ADMIN]
  if (profile.isBrandManager) return BRAND_MANAGER_ASSIGNABLE.has(code)
  if (profile.isAgencyManager) return code === ROLE_CODES.AGENCY_OPERATOR
  return false
}

export const getVisibleRoles = (profile, roles = []) => roles.filter(role => canSeeRole(profile, role))

export const canAssignRole = (profile, role) => {
  if (!canSeeRole(profile, role)) return false
  const code = normalizeRoleCode(role.code)
  if (profile.isSuperAdmin) return true
  if (profile.isSystemAdmin) return code !== ROLE_CODES.SYSTEM_SUPER_ADMIN
  if (profile.isBrandManager) return BRAND_MANAGER_ASSIGNABLE.has(code)
  return false
}

export const getAssignableRoleOptions = (profile, roles = []) => roles.filter(role => canAssignRole(profile, role))

export const roleLabel = profile => {
  if (!profile) return 'Loading permissions'
  if (profile.isSuperAdmin) return ROLE_LABELS[ROLE_CODES.SYSTEM_SUPER_ADMIN]
  if (profile.isSystemAdmin) return ROLE_LABELS[ROLE_CODES.SYSTEM_ADMIN]
  if (profile.isSystemOperator) return ROLE_LABELS[ROLE_CODES.SYSTEM_OPERATOR]
  if (profile.isBrandManager) return ROLE_LABELS[ROLE_CODES.BRAND_MANAGER]
  if (profile.roleCodes.has(ROLE_CODES.BRAND_OPERATOR)) return ROLE_LABELS[ROLE_CODES.BRAND_OPERATOR]
  if (profile.isAgencyManager) return ROLE_LABELS[ROLE_CODES.AGENCY_MANAGER]
  if (profile.roleCodes.has(ROLE_CODES.AGENCY_OPERATOR)) return ROLE_LABELS[ROLE_CODES.AGENCY_OPERATOR]
  return profile.accountType || 'User'
}
